import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import EntityMap from "../../components/EntityMap";
import {
  Building2, Building, Map as MapIcon, Plus, Pencil, Trash2, X,
  Briefcase, MapPin,
} from "lucide-react";
import { fmtNumber } from "../../lib/utils";

const TYPE_META = {
  company: { icon: Briefcase, label: "Company", color: "#064E3B" },
  site: { icon: Building2, label: "Site", color: "#10B981" },
  facility: { icon: Building, label: "Facility", color: "#F59E0B" },
};

const APPROACH_LABEL = {
  equity_share: "Equity Share",
  operational: "Operational Control",
  financial: "Financial Control",
};

const EMPTY = {
  name: "",
  type: "company",
  parent_id: null,
  address: { street: "", barangay: "", city: "", province: "", country: "Philippines" },
  latitude: null,
  longitude: null,
  ownership_pct: 100,
  has_operational_control: true,
  has_financial_control: true,
  legal_entity_id: "",
  headcount: null,
  seat_count: null,
  floor_area_sqm: null,
  shift_pattern: "",
  notes: "",
};

export default function Entities() {
  const [entities, setEntities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);  // { type } when creating
  const [filterType, setFilterType] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/entities"),
        api.get("/admin/entities/summary"),
      ]);
      setEntities(a.data.rows || []);
      setSummary(b.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (filterType ? entities.filter((e) => e.type === filterType) : entities),
    [entities, filterType]
  );

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.type} "${e.name}"?`)) return;
    try {
      await api.delete(`/admin/entities/${e.id}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading && !entities.length) return <div className="p-6 text-ink-soft">Loading entities…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="entities-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="section-label">Admin · Organisation Structure</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Companies, Sites & Facilities</h1>
          <p className="text-sm text-ink-soft max-w-3xl">
            Define every legal entity, operating site and building the organisation owns or controls.
            Aligned to <span className="font-mono">GHG Protocol</span>, <span className="font-mono">IFRS S2</span> and <span className="font-mono">ISO 14064</span> —
            each Company carries an ownership % and operational/financial control flags that drive
            consolidated metrics across all ESG reports.
          </p>
        </div>
        <div className="flex gap-2">
          {Object.keys(TYPE_META).map((t) => (
            <button
              key={t}
              data-testid={`add-${t}`}
              onClick={() => setCreating({ ...EMPTY, type: t,
                ownership_pct: t === "company" ? 100 : null,
                has_operational_control: t === "company" ? true : null,
                has_financial_control: t === "company" ? true : null,
              })}
              className="btn btn-primary"
            >
              <Plus size={12} /> Add {TYPE_META[t].label}
            </button>
          ))}
        </div>
      </div>

      {/* Consolidation summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="entities-summary">
          <SummaryTile
            label="Consolidation"
            value={APPROACH_LABEL[summary.consolidation_approach] || summary.consolidation_approach}
            sub={`Factor ${(summary.current.average_factor * 100).toFixed(1)}%`}
            color="#064E3B"
            testid="tile-consolidation"
          />
          <SummaryTile
            label="Companies"
            value={summary.counts.companies}
            sub={`${summary.current.fully_consolidated_companies} full · ${summary.current.partial_companies} partial · ${summary.current.excluded_companies} excl.`}
            color="#064E3B"
            testid="tile-companies"
          />
          <SummaryTile
            label="Sites"
            value={summary.counts.sites}
            sub={`${summary.site_totals.headcount.toLocaleString()} FTE · ${summary.site_totals.seat_count.toLocaleString()} seats`}
            color="#10B981"
            testid="tile-sites"
          />
          <SummaryTile
            label="Facilities"
            value={summary.counts.facilities}
            sub={`${fmtNumber(summary.site_totals.floor_area_sqm, 0)} m² total`}
            color="#F59E0B"
            testid="tile-facilities"
          />
        </div>
      )}

      {/* Map + per-approach factor table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-4 lg:col-span-2 overflow-hidden" data-testid="entities-map-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="section-label flex items-center gap-2"><MapIcon size={12}/> Geographic footprint</div>
              <div className="text-xs text-ink-soft mt-0.5">
                OpenStreetMap tiles · click a marker for details.
              </div>
            </div>
            <div className="text-[10px] text-ink-soft">{entities.filter(e => e.latitude && e.longitude).length} mapped of {entities.length}</div>
          </div>
          <EntityMap entities={entities} onSelect={(e) => setEditing(e)} />
        </div>

        <div className="card p-4" data-testid="approach-card">
          <div className="section-label">Consolidation approach factors</div>
          <div className="text-xs text-ink-soft mt-0.5">
            Per GHG Protocol Corporate Standard. Active approach highlighted.
          </div>
          {summary && (
            <table className="dense w-full mt-3">
              <thead>
                <tr>
                  <th>Approach</th>
                  <th className="text-right">Avg</th>
                  <th className="text-right">Full</th>
                  <th className="text-right">Partial</th>
                  <th className="text-right">Excl.</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.per_approach).map(([k, v]) => (
                  <tr key={k} className={k === summary.consolidation_approach ? "bg-emerald-50" : ""} data-testid={`approach-row-${k}`}>
                    <td className="font-medium">
                      {APPROACH_LABEL[k]}
                      {k === summary.consolidation_approach && <span className="pill pill-success ml-2 text-[10px]">active</span>}
                    </td>
                    <td className="num font-mono">{(v.average_factor * 100).toFixed(1)}%</td>
                    <td className="num">{v.fully_consolidated_companies}</td>
                    <td className="num">{v.partial_companies}</td>
                    <td className="num">{v.excluded_companies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 text-[11px] text-ink-soft border-t border-border pt-2">
            Change the active approach in <span className="font-mono">Organisation Settings → Consolidation approach</span>.
            All KPI scorecards and reports use the active approach to scale Scope 1/2/3 totals.
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2">
        <span className="dlabel">Filter:</span>
        <button onClick={() => setFilterType("")} className={"pill " + (!filterType ? "pill-success" : "")} data-testid="filter-all">All</button>
        {Object.entries(TYPE_META).map(([k, m]) => (
          <button key={k} onClick={() => setFilterType(k)} className={"pill " + (filterType === k ? "pill-success" : "")} data-testid={`filter-${k}`}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Entity table */}
      <div className="card overflow-hidden" data-testid="entities-table">
        <table className="dense">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Parent</th>
              <th>Address</th>
              <th className="text-right">Ownership / Control</th>
              <th className="text-right">Headcount</th>
              <th className="text-right">Seats</th>
              <th className="text-right">Area (m²)</th>
              <th>Shift</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const Icon = TYPE_META[e.type].icon;
              const parent = entities.find((p) => p.id === e.parent_id);
              return (
                <tr key={e.id} data-testid={`entity-row-${e.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <Icon size={12} style={{ color: TYPE_META[e.type].color }} />
                      <span className="font-medium">{e.name}</span>
                    </div>
                    {e.legal_entity_id && <div className="text-[10px] text-ink-soft font-mono">{e.legal_entity_id}</div>}
                  </td>
                  <td><span className="pill text-[10px]">{TYPE_META[e.type].label}</span></td>
                  <td className="text-[11px] text-ink-soft">{parent?.name || "—"}</td>
                  <td className="text-[11px]">
                    <div className="flex items-center gap-1">
                      <MapPin size={10} className="text-ink-soft"/>
                      {[e.address?.barangay, e.address?.city, e.address?.province].filter(Boolean).join(", ") || "—"}
                    </div>
                    {e.latitude && e.longitude && (
                      <div className="text-[10px] text-ink-soft font-mono">
                        {e.latitude.toFixed(4)}, {e.longitude.toFixed(4)}
                      </div>
                    )}
                  </td>
                  <td className="num text-[11px]">
                    {e.type === "company" ? (
                      <div className="space-y-0.5">
                        <div>{e.ownership_pct}% owned</div>
                        <div className="text-ink-soft">
                          {e.has_operational_control ? "✓ Op" : "—"} · {e.has_financial_control ? "✓ Fin" : "—"}
                        </div>
                        <div className="text-[10px] text-brand font-mono">
                          factor {(e.consolidation_factor * 100).toFixed(1)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-ink-soft text-[10px]">
                        inherits {e.owning_company_name ? `from ${e.owning_company_name}` : "—"}
                      </span>
                    )}
                  </td>
                  <td className="num">{e.headcount != null ? fmtNumber(e.headcount, 0) : "—"}</td>
                  <td className="num">{e.seat_count != null ? fmtNumber(e.seat_count, 0) : "—"}</td>
                  <td className="num">{e.floor_area_sqm != null ? fmtNumber(e.floor_area_sqm, 0) : "—"}</td>
                  <td className="text-[11px] text-ink-soft">{e.shift_pattern || "—"}</td>
                  <td className="text-right whitespace-nowrap">
                    <button data-testid={`edit-${e.id}`} onClick={() => setEditing(e)} className="text-ink-soft hover:text-brand p-1"><Pencil size={12}/></button>
                    <button data-testid={`delete-${e.id}`} onClick={() => remove(e)} className="text-ink-soft hover:text-status-error p-1 ml-1"><Trash2 size={12}/></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center text-ink-soft py-6 text-xs">No entities — add a Company to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editing || creating) && (
        <EntityForm
          initial={editing || creating}
          isEdit={!!editing}
          allEntities={entities}
          onClose={() => { setEditing(null); setCreating(null); }}
          onSaved={async () => { setEditing(null); setCreating(null); await load(); }}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, color, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="dot" style={{ background: color }} />
        <div className="section-label">{label}</div>
      </div>
      <div className="metric-value mt-1">{value}</div>
      <div className="text-[11px] text-ink-soft">{sub}</div>
    </div>
  );
}

function EntityForm({ initial, isEdit, allEntities, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...initial,
    address: initial.address || { street: "", barangay: "", city: "", province: "", country: "Philippines" },
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateAddr = (patch) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }));

  const eligibleParents = useMemo(() => {
    if (form.type === "company") return [];
    if (form.type === "site") return allEntities.filter((e) => e.type === "company");
    return allEntities.filter((e) => e.type === "company" || e.type === "site");
  }, [form.type, allEntities]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name,
        type: form.type,
        parent_id: form.parent_id || null,
        address: form.address,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        ownership_pct: form.type === "company" ? Number(form.ownership_pct || 0) : null,
        has_operational_control: form.type === "company" ? !!form.has_operational_control : null,
        has_financial_control: form.type === "company" ? !!form.has_financial_control : null,
        legal_entity_id: form.legal_entity_id || null,
        headcount: form.headcount ? Number(form.headcount) : null,
        seat_count: form.seat_count ? Number(form.seat_count) : null,
        floor_area_sqm: form.floor_area_sqm ? Number(form.floor_area_sqm) : null,
        shift_pattern: form.shift_pattern || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await api.put(`/admin/entities/${initial.id}`, payload);
      } else {
        await api.post("/admin/entities", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const showCompany = form.type === "company";
  const showSite = form.type === "site" || form.type === "facility";

  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="entity-form-modal">
      <div className="card w-full max-w-3xl bg-white max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="section-label">{isEdit ? "Edit" : "Create"} {TYPE_META[form.type]?.label}</div>
            <div className="text-xs text-ink-soft mt-0.5">{form.name || "New entity"}</div>
          </div>
          <button onClick={onClose} className="btn btn-ghost" data-testid="entity-form-close"><X size={14}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="dlabel">Name</label>
              <input data-testid="ef-name" required value={form.name} onChange={(e) => update({ name: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
            </div>
            <div>
              <label className="dlabel">Type</label>
              <select data-testid="ef-type" value={form.type} disabled={isEdit}
                onChange={(e) => update({ type: e.target.value, parent_id: null })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm">
                {Object.entries(TYPE_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {form.type !== "company" && (
            <div>
              <label className="dlabel">Parent {form.type === "site" ? "company" : "company or site"}</label>
              <select data-testid="ef-parent" required value={form.parent_id || ""}
                onChange={(e) => update({ parent_id: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm">
                <option value="">— select parent —</option>
                {eligibleParents.map((p) => (
                  <option key={p.id} value={p.id}>{TYPE_META[p.type].label} · {p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Address block */}
          <div className="border border-border rounded-md p-3 bg-surface/40">
            <div className="dlabel mb-2">Address</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="dlabel">Street</label>
                <input data-testid="ef-street" value={form.address.street || ""} onChange={(e) => updateAddr({ street: e.target.value })}
                  className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
              </div>
              <div>
                <label className="dlabel">Barangay</label>
                <input data-testid="ef-barangay" value={form.address.barangay || ""} onChange={(e) => updateAddr({ barangay: e.target.value })}
                  className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
              </div>
              <div>
                <label className="dlabel">City / Municipality</label>
                <input data-testid="ef-city" value={form.address.city || ""} onChange={(e) => updateAddr({ city: e.target.value })}
                  className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
              </div>
              <div>
                <label className="dlabel">Province</label>
                <input data-testid="ef-province" value={form.address.province || ""} onChange={(e) => updateAddr({ province: e.target.value })}
                  className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
              </div>
              <div>
                <label className="dlabel">Country</label>
                <input data-testid="ef-country" value={form.address.country || "Philippines"} onChange={(e) => updateAddr({ country: e.target.value })}
                  className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="dlabel">Latitude</label>
                <input data-testid="ef-lat" type="number" step="any" value={form.latitude ?? ""} onChange={(e) => update({ latitude: e.target.value })}
                  placeholder="14.5547" className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
              </div>
              <div>
                <label className="dlabel">Longitude</label>
                <input data-testid="ef-lng" type="number" step="any" value={form.longitude ?? ""} onChange={(e) => update({ longitude: e.target.value })}
                  placeholder="121.0244" className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
              </div>
            </div>
          </div>

          {showCompany && (
            <div className="border border-border rounded-md p-3 bg-emerald-50/40">
              <div className="dlabel mb-2">Ownership & Control (GHG Protocol)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="dlabel">Ownership %</label>
                  <input data-testid="ef-ownership-pct" type="number" min="0" max="100" step="any"
                    value={form.ownership_pct ?? ""} onChange={(e) => update({ ownership_pct: e.target.value })}
                    className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
                </div>
                <div>
                  <label className="dlabel">Legal entity ID (optional)</label>
                  <input data-testid="ef-legal-id" value={form.legal_entity_id || ""} onChange={(e) => update({ legal_entity_id: e.target.value })}
                    placeholder="SEC-CS200712345" className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <label className="inline-flex items-center gap-2 text-sm" data-testid="ef-op-control-label">
                  <input data-testid="ef-op-control" type="checkbox" checked={!!form.has_operational_control}
                    onChange={(e) => update({ has_operational_control: e.target.checked })}/>
                  <span>Has operational control</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm" data-testid="ef-fin-control-label">
                  <input data-testid="ef-fin-control" type="checkbox" checked={!!form.has_financial_control}
                    onChange={(e) => update({ has_financial_control: e.target.checked })}/>
                  <span>Has financial control</span>
                </label>
              </div>
              <div className="text-[11px] text-ink-soft mt-2">
                Consolidation factor under each GHG Protocol approach:
                <span className="font-mono mx-1">equity = {Number(form.ownership_pct || 0).toFixed(1)}%</span>·
                <span className="font-mono mx-1">operational = {form.has_operational_control ? "100%" : "0%"}</span>·
                <span className="font-mono mx-1">financial = {form.has_financial_control ? "100%" : "0%"}</span>
              </div>
            </div>
          )}

          {showSite && (
            <div className="border border-border rounded-md p-3 bg-emerald-50/30">
              <div className="dlabel mb-2">Site attributes</div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="dlabel">Headcount</label>
                  <input data-testid="ef-headcount" type="number" value={form.headcount ?? ""}
                    onChange={(e) => update({ headcount: e.target.value })}
                    className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
                </div>
                <div>
                  <label className="dlabel">Seat count</label>
                  <input data-testid="ef-seats" type="number" value={form.seat_count ?? ""}
                    onChange={(e) => update({ seat_count: e.target.value })}
                    className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
                </div>
                <div>
                  <label className="dlabel">Floor area (m²)</label>
                  <input data-testid="ef-floor" type="number" step="any" value={form.floor_area_sqm ?? ""}
                    onChange={(e) => update({ floor_area_sqm: e.target.value })}
                    className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"/>
                </div>
                <div>
                  <label className="dlabel">Shift pattern</label>
                  <input data-testid="ef-shift" value={form.shift_pattern || ""}
                    onChange={(e) => update({ shift_pattern: e.target.value })}
                    placeholder="24/7 · 3-shift" className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="dlabel">Notes (optional)</label>
            <textarea data-testid="ef-notes" value={form.notes || ""} onChange={(e) => update({ notes: e.target.value })}
              className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm h-16"/>
          </div>

          {err && <div className="text-status-error text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button data-testid="ef-submit" disabled={busy} type="submit" className="btn btn-primary">
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
